import type { PropsElement } from '../props';
import type { Bounds, ToolFn, ToolSpec } from '../types';
import { distance } from './geometry';
import { ofType, round } from './select';

export const nearSpec: ToolSpec = {
  name: 'elements_near',
  description:
    'Elements within `radius` metres of one element (by expressID), nearest first, optionally only of one type. Needs the 3D geometry, which the site provides.',
  input_schema: {
    type: 'object',
    properties: {
      id: { type: 'integer' },
      radius: { type: 'number', description: 'metres, default 3' },
      type: { type: 'string' },
      limit: { type: 'integer' },
    },
    required: ['id'],
  },
};

export const near =
  (elements: PropsElement[], bounds?: Record<number, Bounds>): ToolFn =>
  ({ id, radius, type, limit }) => {
    if (!bounds) return { error: 'no geometry available' };
    const origin = bounds[Number(id)];
    if (!origin) return { error: `no geometry for element ${id}` };
    const r = Number(radius) || 3;
    const max = Math.min(Number(limit) || 15, 40);
    const hits = ofType(elements, type)
      .filter((e) => e.expressID !== Number(id) && bounds[e.expressID])
      .map((e) => ({ e, d: distance(origin, bounds[e.expressID]!) }))
      .filter((x) => x.d <= r)
      .sort((a, b) => a.d - b.d);
    return {
      count: hits.length,
      radius: r,
      elements: hits.slice(0, max).map(({ e, d }) => ({
        id: e.expressID,
        type: e.type,
        name: e.name.slice(0, 60),
        storey: e.storey,
        distance_m: round(d, 2),
        ...(e.material ? { material: e.material.names.join(' | ') } : {}),
      })),
      ids: hits.map((x) => x.e.expressID),
    };
  };
